var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Subject } from "rxjs";
import { CallService } from "./call.service";
import { FamilyService } from "./family.service";
import { FormationService } from "./formation.service";
import { PersistenceService } from "./persistence.service";
import { Collection } from "../models/collection";
import { ModuleService } from "./module.service";
import { CachingModelService } from "./caching-model.service";
import { SyncService } from "./sync.service";
import { ToastrService } from "ngx-toastr";
export class CollectionService extends CachingModelService {
    constructor(callSvc, familySvc, formationSvc, moduleSvc, persistSvc, syncSvc, toastr) {
        super();
        this.callSvc = callSvc;
        this.familySvc = familySvc;
        this.formationSvc = formationSvc;
        this.moduleSvc = moduleSvc;
        this.persistSvc = persistSvc;
        this.syncSvc = syncSvc;
        this.toastr = toastr;
        this.user = null;
        this.changed$ = new Subject();
    }
    createNew() {
        const c = Collection.forUser(this.user);
        this.syncSvc.setDirty(this.user);
        this.add(c);
        this.changed$.next();
        return c;
    }
    clearAll() {
        super.clearAll();
        this.callSvc.clearAll();
        this.familySvc.clearAll();
        this.formationSvc.clearAll();
        this.moduleSvc.clearAll();
        this.user = null;
        this.changed$.next();
    }
    loadFrom(user) {
        return __awaiter(this, void 0, void 0, function* () {
            this.clearAll();
            this.user = user;
            try {
                (yield this.persistSvc.loadUserCollectionsFromCloud())
                    .map(collection => this.loaded(collection));
                for (const collectionPath of Array.from(user.collections)) {
                    const c = yield this.persistSvc.loadCollection(collectionPath);
                    this.loaded(c);
                }
            }
            catch (err) {
                console.error(err);
                this.toastr.error(err.toString(), 'Sync Failure', { timeOut: 10000 });
            }
            if (this.size > 0) {
                this.toastr.success('Loaded ' + this.size, "Collections");
            }
            this.resolveReferences();
        });
    }
    loaded(c) {
        const old = this.get(c.id);
        if (old && c.modified.getTime() === old.modified.getTime()) {
            return false;
        }
        else {
            this.add(c);
            return true;
        }
    }
    resolveReferences() {
        this.callSvc.clearAll();
        this.familySvc.clearAll();
        this.formationSvc.clearAll();
        this.moduleSvc.clearAll();
        super.forEach(collection => {
            collection.formations.forEach(f => this.formationSvc.add(f));
            collection.families.forEach(f => this.familySvc.add(f));
            collection.calls.forEach(call => this.callSvc.add(call));
            collection.modules.forEach(m => {
                m.collection = collection;
                this.moduleSvc.add(m);
            });
        });
        this.formationSvc.resolveReferences();
        this.familySvc.resolveReferences();
        this.callSvc.resolveReferences();
        this.moduleSvc.resolveReferences();
        this.forEach(c => {
            c.isAvailable =
                c.calls.find((call) => !call.isAvailable) == undefined &&
                    c.modules.find((m) => !m.isAvailable) == undefined;
        });
        this.changed$.next();
    }
    localSave(collection) {
        if (collection) {
            if (collection.isDirty) {
                return this.persistSvc.localStoreCollection(collection);
            }
            else {
                return Promise.resolve();
            }
        }
        else {
            const queue = [];
            this.forEach(c => {
                if (c.isDirty) {
                    queue.push(this.persistSvc.localStoreCollection(c));
                }
            });
            return Promise.all(queue);
        }
    }
    sync(sendOnly) {
        return __awaiter(this, void 0, void 0, function* () {
            let needToResolve = false;
            try {
                for (const collection of Array.from(this.values())) {
                    const isMine = collection.authorUserId === this.user.id;
                    if (isMine && (collection.isDirty || !collection.isCloudBacked)) {
                        console.log(`Collection ${collection.id} has been locally changed. Save it.`);
                        yield this.persistSvc.cloudSaveCollection(collection);
                        this.toastr.success("Saved", collection.name);
                    }
                    else if (!sendOnly && (yield this.persistSvc.isNewerInCloud(collection))) {
                        console.log(`Load updated content from server for collection ${collection.id}`);
                        const c = yield this.persistSvc.loadCollection(collection);
                        const hasChanged = this.loaded(c);
                        if (hasChanged) {
                            needToResolve = true;
                            this.toastr.warning("Updated", c.name);
                        }
                    }
                }
                if (needToResolve) {
                    this.resolveReferences();
                }
                else {
                    this.toastr.info("No updates received");
                }
            }
            catch (err) {
                console.error(err);
                return this.toastr.error(err.toString(), 'Sync Failure', { timeOut: 10000 });
            }
        });
    }
    loadHistory(collection) {
        return this.persistSvc.loadHistory(collection);
    }
    restoreFromCloud(collectionId) {
        return this.persistSvc.cloudReloadCollection(collectionId)
            .then(c => {
            this.add(c);
            this.resolveReferences();
            return c;
        });
    }
    getVisibilityIcon(c) {
        if (!c) {
            return "glyphicon glyphicon-warning-sign";
        }
        else if (c.authorUserId !== this.user.id) {
            return "glyphicon glyphicon-globe";
        }
        else if (c.isPublic) {
            return "glyphicon glyphicon-gift";
        }
        else {
            return "glyphicon glyphicon-lock";
        }
    }
}
